import Slider from "react-slick";
import {
  width,
  height,
  position,
  zIndex,
  top,
  left,
  right,
  overflow,
  size
} from "styled-system";
import { Box } from "rebass";
import styled from "styled-components";
import { LazyImage } from "./lazy-image";
import { Arrow } from "./arrow";
import t from "typy";

// slides: { alt, url }[]
const Carousel = ({ slides, onBeforeChange }) => {
  // Filters out blank repeatables
  // Prismic treats them as empty objects so
  // a length check isn't enough
  const slides_filtered = slides.filter(
    slide =>
      Object.entries(slide.image).length > 0 &&
      slide.image.constructor === Object
  );

  const hasSingleSlide =
    t(slides_filtered, "length").isDefined && slides_filtered.length === 1;
  const hasMultipleSlides =
    t(slides_filtered, "length").isDefined && slides_filtered.length > 1;

  let myslider;
  let centerSettings = {};
  if (hasMultipleSlides) {
    centerSettings.centerMode = true;
    centerSettings.centerPadding = "10%";
  }
  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    ...centerSettings,
    beforeChange: (current, next) => handleBeforeChange(current, next)
  };

  const handleBeforeChange = (current, next) => {
    if (onBeforeChange) {
      onBeforeChange(current, next);
    }
  };

  const prevSlide = () => {
    myslider.slickPrev();
  };

  const nextSlide = () => {
    myslider.slickNext();
  };

  return (
    <Wrap position="relative" overflow="hidden">
      {hasMultipleSlides && (
        <>
          <ArrowWrapper left={[20, 20, 20, 30]} aria-label="Move carousel left">
            <StyledArrow direction="left" size={[30, 50]} onClick={prevSlide} />
          </ArrowWrapper>
          <S_Slider
            position="relative"
            ref={slider => (myslider = slider)}
            {...settings}
          >
            {slides_filtered.map((slide, i) => {
              return (
                <Container key={i} position="relative" height={[180, 300, 500]}>
                  <BackgroundImage
                    width={1}
                    height={["100%"]}
                    z-index={1}
                    src={slide.image.url}
                    alt={slide.image.alt}
                  />
                  <Skrim
                    position="absolute"
                    top={0}
                    left={0}
                    width={1}
                    height={["100%"]}
                    zIndex={3}
                  />
                </Container>
              );
            })}
          </S_Slider>
          <ArrowWrapper
            right={[20, 20, 20, 30]}
            aria-label="Move carousel right"
          >
            <StyledArrow
              direction="right"
              size={[30, 50]}
              onClick={nextSlide}
            />
          </ArrowWrapper>
        </>
      )}
      {hasSingleSlide && (
        <SingleImage
          height={[180, 300, 500]}
          src={slides_filtered[0].image.url}
          alt={slides_filtered[0].image.alt}
        />
      )}
    </Wrap>
  );
};

const SingleImage = styled.img`
  ${height};
  width: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
`;

const ArrowWrapper = styled(Box)`
  ${left}
  ${right}
  position: absolute;
  top: 50%;
  z-index: 3;
`;

const StyledArrow = styled(Arrow)`
  ${size}
  cursor: pointer;
`;

const Skrim = styled(Box)`
  ${position}
  ${top}
  ${left}
  ${width}
  ${height}
  ${zIndex}
  background: linear-gradient(
    200deg,
    rgba(150, 211, 239, 1) 0%,
    rgba(255, 255, 255, 1) 70%,
    rgba(255, 229, 136, 1) 100%
  );
  opacity: 0.7;
  transition: all 1s ease-in;
`;

const Wrap = styled(Box)`
  ${position}
  ${overflow}

  line-height: 0;

  .slick-track {
    display: flex;
  }

  .slick-active,
  .slick-current {
    ${Skrim} {
      opacity: 0;
    }
  }
`;

const BackgroundImage = styled(LazyImage)`
  ${zIndex}
  ${width}
  ${height}
  display: block;
  object-fit: cover;
`;

const Container = styled(Box)`
  ${height};
  ${position};
  pointer-events: none;

  a,
  div {
    display: block;
    height: 100%;
  }
`;

const S_Slider = styled(Slider)`
  ${position}

  .slick-current {
    ${Container} {
      pointer-events: auto;
    }
  }
`;

export { Carousel };
